// A clerk's statement is the clerk's own list of what is still owed, as of its date. So once
// a clerk has a statement, that clerk's other unpaid items - emailed invoices and reminders,
// fee slips, backsheet invoices - are only still owing if the statement could not have
// included them (Emma, 16 Oct 2026):
//
//   - the same invoice as a row on the statement: already counted there, so not counted
//     again (reminder emails for invoices on the statement were doubling clerks' totals);
//   - not on the statement, and dated on or before the statement's date: it would be on the
//     statement if it were still owed, so it has been paid;
//   - not on the statement, and dated after it: arrived since, still owing.
//
// An emailed invoice is dated by the last time it was sent, re-sent copies included: a
// clerk chasing it after their statement says it is still owed, whatever the statement
// left out (26 Oct 2026: Janey Fisher's Dever's invoices 949041 and 949042, first emailed
// in August and chased on 25 October, were hidden as paid).
//
// An unallocated emailed invoice whose number is on a clerk's statement is the first kind too,
// wherever it was filed.
//
// The same invoice can also arrive more than once: from the clerk, forwarded by staff, chased
// a month later. So one invoice number in one clerk's list is shown once (Emma, 3 Nov 2026),
// and an unallocated copy of an invoice already in a clerk's list is not shown at all. A copy
// stays if anyone has acted on it (a status or a note), or if its amount differs, which may
// mean a different bill carrying the number of the email it came with.
//
// This is read-time only. Nothing is marked paid or deleted; these are left out of the list
// and its totals, and the page can show them again.

import { normalizePaymentStatus } from "./clerkStatements.js";
import { EMAIL_ONLY_GROUPS } from "./emailIntake.js";

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const pad = (n) => String(n).padStart(2, "0");

// "30/09/2026", "30 September 2026", "2026-09-30T..." -> "2026-09-30"; anything else -> "".
export function isoDay(value = "") {
  const text = String(value || "").trim();
  let m = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return `${m[3]}-${pad(m[2])}-${pad(m[1])}`;
  m = text.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
  if (m) {
    const month = MONTHS.indexOf(m[2].slice(0, 3).toLowerCase());
    if (month !== -1) return `${m[3]}-${pad(month + 1)}-${pad(m[1])}`;
  }
  return "";
}

// "06012508", "6012508", "Invoice 06012508" -> "6012508". Too short to be an invoice number -> "".
export function invoiceKey(value = "") {
  const digits = String(value || "").replace(/\D/g, "").replace(/^0+/, "");
  return digits.length >= 4 ? digits : "";
}

export const COVER_REASONS = {
  on_statement: "on the statement",
  before_statement: "dated on or before the statement and not on it",
  same_invoice: "the same invoice as another item in the list",
};

const isUnfiled = (row) => EMAIL_ONLY_GROUPS.includes(row.clerkName);
const amountOf = (row) => Math.round((Number.parseFloat(row.invoiceAmount) || 0) * 100) / 100;
// Numbers shorter than this are other suppliers' ("INV-1311"), and two suppliers can share one.
const FULL_INVOICE_NUMBER = 6;

// The later of when an item arrived and when it was last sent.
function latestDay(row) {
  const days = [isoDay(row.uploadedAt), isoDay(row.lastSentAt)].filter(Boolean).sort();
  return days.at(-1) || "";
}

// Which copy of an invoice to keep showing. Whoever has acted on one, then the one in a clerk's
// list, then the one with an amount, then the first to arrive.
function copyRank(row, statusOf) {
  const status = normalizePaymentStatus(statusOf(row));
  return [
    status === "paid" ? 0 : 1,
    status !== "not_paid" ? 0 : 1,
    String(row.notes || "").trim() ? 0 : 1,
    isUnfiled(row) ? 1 : 0,
    amountOf(row) > 0 ? 0 : 1,
    row.source === "email" ? 1 : 0,
    row.possibleDuplicate ? 1 : 0,
  ];
}

function compareCopies(a, b, statusOf) {
  const left = copyRank(a, statusOf);
  const right = copyRank(b, statusOf);
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return left[index] - right[index];
  }
  return String(a.uploadedAt || "").localeCompare(String(b.uploadedAt || ""))
    || String(a.id).localeCompare(String(b.id));
}

export function statementCover(rows = [], statusOf = (row) => row.status) {
  const statements = new Map();
  const statementOfInvoice = new Map();
  for (const row of rows) {
    if (row.source !== "clerk_statement") continue;
    const clerk = row.clerkName;
    if (!statements.has(clerk)) statements.set(clerk, { asOf: "", keys: new Set() });
    const statement = statements.get(clerk);
    const asOf = isoDay(row.statementAsOf);
    if (asOf > statement.asOf) statement.asOf = asOf;
    const key = invoiceKey(row.invoiceName);
    if (key) {
      statement.keys.add(key);
      statementOfInvoice.set(key, clerk);
    }
  }

  const covered = new Map();
  const byClerk = new Map();
  const leaveOut = (row, reason, clerk) => {
    covered.set(row.id, reason);
    if (!byClerk.has(clerk)) {
      byClerk.set(clerk, {
        asOf: statements.get(clerk)?.asOf || "",
        on_statement: { count: 0, total: 0 },
        before_statement: { count: 0, total: 0 },
        same_invoice: { count: 0, total: 0 },
      });
    }
    const tally = byClerk.get(clerk)[reason];
    tally.count += 1;
    tally.total = Math.round((tally.total + (Number.parseFloat(row.invoiceAmount) || 0)) * 100) / 100;
  };

  for (const row of rows) {
    if (row.source === "clerk_statement") continue;
    const key = invoiceKey(row.invoiceRef);
    const unfiled = isUnfiled(row);
    // A statement cannot say an unallocated item was paid (whose statement would it be?), but
    // it can say it is the same invoice.
    const clerk = unfiled && key.length >= FULL_INVOICE_NUMBER ? statementOfInvoice.get(key) : row.clerkName;
    const statement = statements.get(clerk);
    if (!statement) continue;
    if (normalizePaymentStatus(statusOf(row)) === "paid") continue;
    let reason = "";
    if (key && statement.keys.has(key)) reason = "on_statement";
    else if (!unfiled && statement.asOf && latestDay(row) && latestDay(row) <= statement.asOf) reason = "before_statement";
    if (reason) leaveOut(row, reason, clerk);
  }

  // Copies of one invoice: within a clerk's list, and from "From email" into the clerk's list.
  const clerkOfInvoice = new Map();
  const copies = new Map();
  const others = rows.filter((row) => row.source !== "clerk_statement" && invoiceKey(row.invoiceRef));
  for (const row of others) {
    const key = invoiceKey(row.invoiceRef);
    if (!isUnfiled(row) && !clerkOfInvoice.has(key)) clerkOfInvoice.set(key, row.clerkName);
  }
  for (const row of others) {
    const key = invoiceKey(row.invoiceRef);
    if (isUnfiled(row) && key.length < FULL_INVOICE_NUMBER) continue;
    const list = isUnfiled(row) ? clerkOfInvoice.get(key) || row.clerkName : row.clerkName;
    const id = `${isUnfiled(row) && !clerkOfInvoice.has(key) ? "unfiled" : list}|${key}`;
    if (!copies.has(id)) copies.set(id, []);
    copies.get(id).push({ row, list });
  }

  const confirmed = new Set();
  for (const group of copies.values()) {
    if (group.length < 2) continue;
    const ordered = [...group].sort((a, b) => compareCopies(a.row, b.row, statusOf));
    const kept = ordered[0].row;
    // A copy already left out (paid before the statement) still answers for the others.
    const anyLeftOut = group.some(({ row }) => covered.has(row.id));
    for (const { row, list } of ordered.slice(anyLeftOut ? 0 : 1)) {
      if (covered.has(row.id)) continue;
      if (normalizePaymentStatus(statusOf(row)) !== "not_paid" || String(row.notes || "").trim()) continue;
      if (amountOf(row) > 0 && amountOf(kept) > 0 && amountOf(row) !== amountOf(kept)) continue;
      leaveOut(row, "same_invoice", list);
      confirmed.add(kept.id);
    }
  }

  return {
    // The copy left showing has been checked against the others, so its warning has done its job.
    visible: rows
      .filter((row) => !covered.has(row.id))
      .map((row) => (row.possibleDuplicate && confirmed.has(row.id) ? { ...row, possibleDuplicate: false } : row)),
    covered,
    byClerk,
    count: covered.size,
  };
}

// "2026-09-30" -> "30/09/2026", for the note on the page.
export function auDay(iso = "") {
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}
